import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { canActivate } from './core/firebase/auth-guard';
import { applyChoiceGuard } from './pages/apply-choice/apply-choice.links';
import {
  authGuard,
  redirectLoggedInToTarget,
  redirectLoggedInToHome,
  redirectLoggedInToReturnTarget,
  isCurrentUserAuthGuard,
  hrToolGuard,
  schoolProfileProFieldsGuard,
  canActivateEmbedGuard,
  verifyEmailGuard,
  requireVerifiedGuard,
  schoolAdminGuard,
  schulgemeideGuard,
  fachkraftOnlyGuard,
  featureGuard,
  confirmLeaveGuard,
} from './core/guards';
import { pendingOrderSetupGuard } from './pages/onboarding/pending-order-setup.guard';
import { EmbedLayoutComponent } from './layouts/embed-layout/embed-layout.component';
import { DefaultLayoutComponent } from './layouts/default-layout/default-layout.component';
/**
 * Shared school profile + edit routes, reused across public, authority, and school-unit contexts.
 * The school ID param name differs: `:id` for public, `:schoolId` for context routes.
 */
function schoolContextRoutes(prefix: string, guards: any[]): Routes {
  const proGuards = [...guards, schoolProfileProFieldsGuard];
  return [
    {
      path: `${prefix}`,
      loadComponent: () =>
        import('./pages/schools/school-profile/school-profile.component').then(
          m => m.SchoolProfileComponent
        ),
      canActivate: guards,
    },
    {
      path: `${prefix}/onboarding`,
      loadComponent: () =>
        import(
          './pages/schools/school-onboarding/school-onboarding.component'
        ).then(m => m.SchoolOnboardingComponent),
      canActivate: guards,
    },
    {
      path: `${prefix}/edit/name`,
      loadComponent: () =>
        import(
          './pages/schools/forms/school-name-form/school-name-form.component'
        ).then(m => m.SchoolNameFormComponent),
      canActivate: guards,
    },
    {
      path: `${prefix}/edit/avatar`,
      loadComponent: () =>
        import(
          './pages/schools/forms/school-avatar-form/school-avatar-form.component'
        ).then(m => m.SchoolAvatarFormComponent),
      canActivate: guards,
    },
    {
      path: `${prefix}/edit/background`,
      loadComponent: () =>
        import(
          './pages/schools/forms/school-background-form/school-background-form.component'
        ).then(m => m.SchoolBackgroundFormComponent),
      canActivate: guards,
    },
    {
      path: `${prefix}/edit/about`,
      loadComponent: () =>
        import(
          './pages/schools/forms/school-about-form/school-about-form.component'
        ).then(m => m.SchoolAboutFormComponent),
      canActivate: guards,
    },
    {
      path: `${prefix}/edit/info`,
      loadComponent: () =>
        import(
          './pages/schools/forms/school-info-form/school-info-form.component'
        ).then(m => m.SchoolInfoFormComponent),
      canActivate: guards,
    },
    {
      path: `${prefix}/edit/pedagogic-focus`,
      loadComponent: () =>
        import(
          './pages/schools/forms/school-focus-form/school-focus-form.component'
        ).then(m => m.SchoolFocusFormComponent),
      canActivate: proGuards,
    },
    {
      path: `${prefix}/edit/media`,
      loadComponent: () =>
        import(
          './pages/schools/forms/school-media-form/school-media-form.component'
        ).then(m => m.SchoolMediaFormComponent),
      canActivate: proGuards,
    },
    {
      path: `${prefix}/edit/documents`,
      loadComponent: () =>
        import(
          './pages/schools/forms/school-documents-form/school-documents-form.component'
        ).then(m => m.SchoolDocumentsFormComponent),
      canActivate: proGuards,
    },
    {
      path: `${prefix}/edit/weoffer`,
      loadComponent: () =>
        import(
          './pages/schools/forms/school-weoffer-form/school-weoffer-form.component'
        ).then(m => m.SchoolWeofferFormComponent),
      canActivate: proGuards,
    },
    {
      path: `${prefix}/edit/permissions`,
      loadComponent: () =>
        import(
          './pages/schools/forms/school-permissions-form/school-permissions-form.component'
        ).then(m => m.SchoolPermissionsFormComponent),
      canActivate: guards,
    },
  ];
}

function schoolContextEditRoutes(prefix: string, guards: any[]): Routes {
  const routes = schoolContextRoutes(prefix, guards);
  // Remove the first two routes: base profile and onboarding
  return routes.slice(2);
}

const routes: Routes = [
  {
    path: '',
    component: DefaultLayoutComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/home/home.component').then(m => m.HomeComponent),
        canActivate: [authGuard, pendingOrderSetupGuard],
      },
      {
        path: 'login',
        loadComponent: () =>
          import('./pages/login/login.component').then(m => m.LoginComponent),
        ...canActivate(
          redirectLoggedInToReturnTarget(redirectLoggedInToTarget)
        ),
      },
      {
        path: 'password-forget',
        loadComponent: () =>
          import(
            './pages/login/password-forget/password-forget.component'
          ).then(m => m.PasswordForgetComponent),
        ...canActivate(redirectLoggedInToTarget),
      },
      {
        path: 'signup',
        loadComponent: () =>
          import('./pages/signup/signup.component').then(
            m => m.SignupComponent
          ),
        ...canActivate(redirectLoggedInToReturnTarget(redirectLoggedInToHome)),
      },
      {
        path: 'signup/secure-password',
        loadComponent: () =>
          import(
            './pages/signup/signup-secure-password/signup-secure-password.component'
          ).then(m => m.SignupSecurePasswordComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./pages/login/password-reset/password-reset.component').then(
            m => m.PasswordResetComponent
          ),
        ...canActivate(redirectLoggedInToTarget),
      },
      //#region profiles
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/profiles/profiles.component').then(
            m => m.ProfilesComponent
          ),
        canActivate: [authGuard],
      },
      {
        path: 'profile/:id',
        loadComponent: () =>
          import(
            './pages/profiles/profile-detail/profile-detail.component'
          ).then(m => m.ProfileDetailComponent),
        canActivate: [authGuard],
      },
      {
        path: 'me/:id',
        loadComponent: () =>
          import(
            './pages/profiles/profile-detail/profile-detail.component'
          ).then(m => m.ProfileDetailComponent),
        canActivate: [authGuard],
      },
      {
        path: 'me/:id/edit/school',
        loadComponent: () =>
          import(
            './pages/profiles/forms/profile-membership-form/profile-membership-form.component'
          ).then(m => m.ProfileMembershipFormComponent),
        canActivate: [isCurrentUserAuthGuard],
      },
      {
        path: 'me/:id/edit/avatar',
        loadComponent: () =>
          import(
            './pages/profiles/forms/profile-avatar-form/profile-avatar-form.component'
          ).then(m => m.ProfileAvatarFormComponent),
        canActivate: [isCurrentUserAuthGuard],
      },
      {
        path: 'me/:id/edit/background',
        loadComponent: () =>
          import(
            './pages/profiles/forms/profile-background-form/profile-background-form.component'
          ).then(m => m.ProfileBackgroundFormComponent),
        canActivate: [isCurrentUserAuthGuard],
      },
      {
        path: 'me/:id/edit/job',
        loadComponent: () =>
          import(
            './pages/profiles/forms/profile-job-form/profile-job-form.component'
          ).then(m => m.ProfileJobFormComponent),
        canActivate: [isCurrentUserAuthGuard],
      },
      {
        path: 'me/:id/edit/employment-type',
        loadComponent: () =>
          import(
            './pages/profiles/forms/profile-employment-type-form/profile-employment-type-form.component'
          ).then(m => m.ProfileEmploymentTypeFormComponent),
        canActivate: [isCurrentUserAuthGuard],
      },
      {
        path: 'me/:id/edit/slogan',
        loadComponent: () =>
          import(
            './pages/profiles/forms/profile-slogan-form/profile-slogan-form.component'
          ).then(m => m.ProfileSloganFormComponent),
        canActivate: [isCurrentUserAuthGuard],
      },
      {
        path: 'me/:id/edit/about',
        loadComponent: () =>
          import(
            './pages/profiles/forms/profile-about-form/profile-about-form.component'
          ).then(m => m.ProfileAboutFormComponent),
        canActivate: [isCurrentUserAuthGuard],
      },
      {
        path: 'me/:id/edit/subjects',
        loadComponent: () =>
          import(
            './pages/profiles/forms/profile-subjects-form/profile-subjects-form.component'
          ).then(m => m.ProfileSubjectsFormComponent),
        canActivate: [isCurrentUserAuthGuard],
      },
      {
        path: 'me/:id/edit/certifications',
        loadComponent: () =>
          import(
            './pages/profiles/forms/profile-certifications-form/profile-certifications-form.component'
          ).then(m => m.ProfileCertificationsFormComponent),
        canActivate: [isCurrentUserAuthGuard],
      },
      {
        path: 'me/:id/edit/experience/:experienceId',
        loadComponent: () =>
          import(
            './pages/profiles/forms/profile-work-experience-form/profile-work-experience-form.component'
          ).then(m => m.ProfileWorkExperienceFormComponent),
        canActivate: [isCurrentUserAuthGuard],
      },
      {
        path: 'me/:id/edit/education/:educationId',
        loadComponent: () =>
          import(
            './pages/profiles/forms/profile-education-form/profile-education-form.component'
          ).then(m => m.ProfileEducationFormComponent),
        canActivate: [isCurrentUserAuthGuard],
      },
      {
        path: 'me/:id/edit/preferences',
        loadComponent: () =>
          import(
            './pages/profiles/forms/profile-preferences-form/profile-preferences-form.component'
          ).then(m => m.ProfilePreferencesFormComponent),
        canActivate: [isCurrentUserAuthGuard],
      },
      {
        path: 'me/:id/edit/languages',
        loadComponent: () =>
          import(
            './pages/profiles/forms/profile-languages-form/profile-languages-form.component'
          ).then(m => m.ProfileLanguagesFormComponent),
        canActivate: [isCurrentUserAuthGuard],
      },
      //#endregion
      //#region school registration/verification process
      {
        path: 'school-registration/verify/:code',
        loadComponent: () =>
          import(
            './pages/register-school/school-verification/school-verification.component'
          ).then(m => m.SchoolVerificationComponent),
        canActivate: [authGuard],
      },
      {
        path: 'canton-registration',
        loadComponent: () =>
          import(
            './pages/register-school/canton-verification/canton-verification.component'
          ).then(m => m.CantonVerificationComponent),
        canActivate: [authGuard],
      },
      //#endregion
      //#region schools
      {
        path: 'schools/:id/onboarding',
        loadComponent: () =>
          import(
            './pages/schools/school-onboarding/school-onboarding.component'
          ).then(m => m.SchoolOnboardingComponent),
        canActivate: [authGuard],
      },
      {
        path: 'schools/:id/edit/name',
        loadComponent: () =>
          import(
            './pages/schools/forms/school-name-form/school-name-form.component'
          ).then(m => m.SchoolNameFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'schools/:id/edit/avatar',
        loadComponent: () =>
          import(
            './pages/schools/forms/school-avatar-form/school-avatar-form.component'
          ).then(m => m.SchoolAvatarFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'schools/:id/edit/background',
        loadComponent: () =>
          import(
            './pages/schools/forms/school-background-form/school-background-form.component'
          ).then(m => m.SchoolBackgroundFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'schools/:id/edit/about',
        loadComponent: () =>
          import(
            './pages/schools/forms/school-about-form/school-about-form.component'
          ).then(m => m.SchoolAboutFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'schools/:id/edit/info',
        loadComponent: () =>
          import(
            './pages/schools/forms/school-info-form/school-info-form.component'
          ).then(m => m.SchoolInfoFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'schools/:id/edit/pedagogic-focus',
        loadComponent: () =>
          import(
            './pages/schools/forms/school-focus-form/school-focus-form.component'
          ).then(m => m.SchoolFocusFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'schools/:id/edit/media',
        loadComponent: () =>
          import(
            './pages/schools/forms/school-media-form/school-media-form.component'
          ).then(m => m.SchoolMediaFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'schools/:id/edit/documents',
        loadComponent: () =>
          import(
            './pages/schools/forms/school-documents-form/school-documents-form.component'
          ).then(m => m.SchoolDocumentsFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'schools/:id/edit/weoffer',
        loadComponent: () =>
          import(
            './pages/schools/forms/school-weoffer-form/school-weoffer-form.component'
          ).then(m => m.SchoolWeofferFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'schools/:id/edit/permissions',
        loadComponent: () =>
          import(
            './pages/schools/forms/school-permissions-form/school-permissions-form.component'
          ).then(m => m.SchoolPermissionsFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'schools',
        loadComponent: () =>
          import('./pages/schools/schools.component').then(
            m => m.SchoolsComponent
          ),
        data: { embedded: false },
      },
      {
        path: 'schools/subscription/:subscription/checkout',
        loadComponent: () =>
          import('./pages/schools/schools.component').then(
            m => m.SchoolsComponent
          ),
        canActivate: [authGuard],
      },
      {
        path: 'schools/:id',
        loadComponent: () =>
          import(
            './pages/schools/school-profile/school-profile.component'
          ).then(m => m.SchoolProfileComponent),
      },
      {
        path: 'schools/:id/initiative/start',
        loadComponent: () =>
          import('./pages/apply-choice/apply-choice.component').then(
            m => m.ApplyChoiceComponent
          ),
        canActivate: [applyChoiceGuard],
        data: { kind: 'initiative' },
      },
      {
        path: 'schools/:id/initiative',
        loadComponent: () =>
          import(
            './pages/schools/school-initiative-apply/school-initiative-apply.component'
          ).then(m => m.SchoolInitiativeApplyComponent),
      },
      {
        path: 'my-schools',
        loadComponent: () =>
          import('./pages/schools/schools-my/schools-my.component').then(
            m => m.SchoolsMyComponent
          ),
        canActivate: [authGuard, schoolAdminGuard],
        data: { breadcrumbs: true },
      },
      {
        path: 'my-organization/:authorityId',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/schools/school-profile/school-profile.component'
          ).then(m => m.SchoolProfileComponent),
        canActivate: [authGuard, schulgemeideGuard],
      },
      {
        path: 'my-organization/:authorityId/structure',
        loadComponent: () =>
          import(
            './pages/organization/my-organization/my-organization.component'
          ).then(m => m.MyOrganizationComponent),
        canActivate: [authGuard, schulgemeideGuard],
        data: { breadcrumbs: true },
      },
      {
        path: 'my-organization/:authorityId/jobs',
        loadComponent: () =>
          import('./pages/schools/schools-jobs/schools-jobs.component').then(
            m => m.SchoolsJobsComponent
          ),
        canActivate: [authGuard, hrToolGuard, schulgemeideGuard],
        data: { breadcrumbs: true, archived: false },
      },
      {
        path: 'my-organization/:authorityId/jobs/archived',
        loadComponent: () =>
          import('./pages/schools/schools-jobs/schools-jobs.component').then(
            m => m.SchoolsJobsComponent
          ),
        canActivate: [authGuard, hrToolGuard, schulgemeideGuard],
        data: { breadcrumbs: true, archived: true },
      },
      {
        path: 'my-organization/:authorityId/jobs/create',
        loadComponent: () =>
          import('./pages/jobs/job-create/job-create.component').then(
            m => m.JobCreateComponent
          ),
        canActivate: [authGuard, hrToolGuard, schulgemeideGuard],
      },
      {
        path: 'my-organization/:authorityId/hr-tool-locked',
        loadComponent: () =>
          import(
            './pages/schools/hr-tool-locked/hr-tool-locked.component'
          ).then(m => m.HrToolLockedComponent),
        canActivate: [authGuard, schulgemeideGuard],
      },
      {
        path: 'my-organization/:authorityId/jobs/:jobId/applicants',
        loadComponent: () =>
          import(
            './pages/schools/schools-jobs-applicants/schools-jobs-applicants.component'
          ).then(m => m.SchoolsJobsApplicantsComponent),
        canActivate: [authGuard, schulgemeideGuard],
        data: { breadcrumbs: true },
      },
      {
        path: 'my-schools/:id/jobs',
        loadComponent: () =>
          import('./pages/schools/schools-jobs/schools-jobs.component').then(
            m => m.SchoolsJobsComponent
          ),
        canActivate: [authGuard, hrToolGuard],
        data: { breadcrumbs: true, archived: false },
      },
      {
        path: 'my-schools/:id/jobs/archived',
        loadComponent: () =>
          import('./pages/schools/schools-jobs/schools-jobs.component').then(
            m => m.SchoolsJobsComponent
          ),
        canActivate: [authGuard, hrToolGuard],
        data: { breadcrumbs: true, archived: true },
      },
      {
        path: 'my-schools/:id/structure',
        loadComponent: () =>
          import(
            './pages/schools/school-structure/school-structure.component'
          ).then(m => m.SchoolStructureComponent),
        // Keep the same guard level as the old structure route.
        // This avoids accidentally blocking the new view during experiments.
        canActivate: [authGuard],
      },
      {
        path: 'my-organization/:authorityId/schools/:schoolId/edit',
        loadComponent: () =>
          import(
            './pages/organization/school-unit-edit/school-unit-edit.component'
          ).then(m => m.SchoolUnitEditComponent),
        canActivate: [authGuard, schulgemeideGuard],
      },
      {
        path: 'my-schools/:id/jobs/create',
        loadComponent: () =>
          import('./pages/jobs/job-create/job-create.component').then(
            m => m.JobCreateComponent
          ),
        canActivate: [authGuard, hrToolGuard],
      },
      {
        path: 'my-schools/:id/hr-tool-locked',
        loadComponent: () =>
          import(
            './pages/schools/hr-tool-locked/hr-tool-locked.component'
          ).then(m => m.HrToolLockedComponent),
        canActivate: [authGuard],
      },
      {
        path: 'my-schools/:id/initiative-applications',
        loadComponent: () =>
          import(
            './pages/applications/initiative-applications/initiative-applications.component'
          ).then(m => m.InitiativeApplicationsComponent),
        canActivate: [authGuard, hrToolGuard],
        canMatch: [featureGuard('applications_v2')],
      },
      {
        path: 'my-organization/:authorityId/initiative-applications',
        loadComponent: () =>
          import(
            './pages/applications/initiative-applications/initiative-applications.component'
          ).then(m => m.InitiativeApplicationsComponent),
        canActivate: [authGuard, hrToolGuard, schulgemeideGuard],
        canMatch: [featureGuard('applications_v2')],
      },
      {
        path: 'my-schools/:id/talent-pool',
        loadComponent: () =>
          import('./pages/applications/talent-pool/talent-pool.component').then(
            m => m.TalentPoolComponent
          ),
        canActivate: [authGuard, hrToolGuard],
        canMatch: [featureGuard('applications_v2')],
      },
      {
        path: 'my-organization/:authorityId/talent-pool',
        loadComponent: () =>
          import('./pages/applications/talent-pool/talent-pool.component').then(
            m => m.TalentPoolComponent
          ),
        canActivate: [authGuard, hrToolGuard, schulgemeideGuard],
        canMatch: [featureGuard('applications_v2')],
      },
      {
        path: 'my-schools/:id/jobs/:jobId/applicants',
        loadComponent: () =>
          import(
            './pages/schools/schools-jobs-applicants/schools-jobs-applicants.component'
          ).then(m => m.SchoolsJobsApplicantsComponent),
        canActivate: [authGuard],
        data: { breadcrumbs: true },
      },
      {
        // Email link target for authority-owned postings (admins arriving from notification mails).
        path: 'my-school-authorities/:authorityId/jobs/:jobId/applicants',
        loadComponent: () =>
          import(
            './pages/schools/schools-jobs-applicants/schools-jobs-applicants.component'
          ).then(m => m.SchoolsJobsApplicantsComponent),
        canActivate: [authGuard, schulgemeideGuard],
        data: { breadcrumbs: true },
      },
      {
        path: 'my-schools/:id/edit/permissions',
        loadComponent: () =>
          import(
            './pages/schools/forms/school-permissions-form/school-permissions-form.component'
          ).then(m => m.SchoolPermissionsFormComponent),
        canActivate: [authGuard],
      },
      //#region context school routes
      ...schoolContextEditRoutes('my-organization/:authorityId', [
        authGuard,
        schulgemeideGuard,
      ]),
      ...schoolContextRoutes('my-organization/:authorityId/schools/:schoolId', [
        authGuard,
        schulgemeideGuard,
      ]),
      ...schoolContextRoutes('my-schools/:id/schools/:schoolId', [
        authGuard,
        schoolAdminGuard,
      ]),
      //#endregion
      //#endregion
      //#region jobs
      {
        path: 'jobs',
        loadComponent: () =>
          import('./pages/jobs/jobs.component').then(m => m.JobsComponent),
        data: { embedded: false },
      },
      {
        path: 'jobs/:id',
        loadComponent: () =>
          import('./pages/jobs/job-detail/job-detail.component').then(
            m => m.JobDetailComponent
          ),
      },
      {
        path: 'jobs/:id/edit/addInfos',
        loadComponent: () =>
          import(
            './pages/jobs/forms/job-add-infos-form/job-add-infos-form.component'
          ).then(m => m.JobAdditionalInfosFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'jobs/:id/edit/date',
        loadComponent: () =>
          import(
            './pages/jobs/forms/job-date-form/job-date-form.component'
          ).then(m => m.JobDateFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'jobs/:id/edit/duration',
        loadComponent: () =>
          import(
            './pages/jobs/forms/job-duration-form/job-duration-form.component'
          ).then(m => m.JobDurationFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'jobs/:id/edit/deadline',
        loadComponent: () =>
          import(
            './pages/jobs/forms/job-application-deadline-form/job-application-deadline-form.component'
          ).then(m => m.JobApplicationDeadlineFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'jobs/:id/edit/function',
        loadComponent: () =>
          import(
            './pages/jobs/forms/job-function-form/job-function-form.component'
          ).then(m => m.JobFunctionFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'jobs/:id/edit/requirements',
        loadComponent: () =>
          import(
            './pages/jobs/forms/job-requirements-form/job-requirements-form.component'
          ).then(m => m.JobRequirementsFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'jobs/:id/edit/schoollevels',
        loadComponent: () =>
          import(
            './pages/jobs/forms/job-school-levels-form/job-school-levels-form.component'
          ).then(m => m.JobSchoolLevelsFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'jobs/:id/edit/skills',
        loadComponent: () =>
          import(
            './pages/jobs/forms/job-skills-form/job-skills-form.component'
          ).then(m => m.JobSkillsFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'jobs/:id/edit/subjects',
        loadComponent: () =>
          import(
            './pages/jobs/forms/job-subjects-form/job-subjects-form.component'
          ).then(m => m.JobSubjectsFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'jobs/:id/edit/tasks',
        loadComponent: () =>
          import(
            './pages/jobs/forms/job-tasks-form/job-tasks-form.component'
          ).then(m => m.JobTasksFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'jobs/:id/edit/weoffer',
        loadComponent: () =>
          import(
            './pages/jobs/forms/job-we-offer-form/job-we-offer-form.component'
          ).then(m => m.JobWeOfferFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'jobs/:id/edit/workload',
        loadComponent: () =>
          import(
            './pages/jobs/forms/job-workload-form/job-workload-form.component'
          ).then(m => m.JobWorkloadFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'jobs/:id/apply',
        loadComponent: () =>
          import('./pages/jobs/job-apply/job-apply.component').then(
            m => m.JobApplyComponent
          ),
        canActivate: [authGuard, fachkraftOnlyGuard],
      },
      {
        path: 'jobs/:id/edit/media',
        loadComponent: () =>
          import(
            './pages/jobs/forms/job-media-form/job-media-form.component'
          ).then(m => m.JobMediaFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'jobs/:id/edit/contacts',
        loadComponent: () =>
          import(
            './pages/jobs/forms/job-contact-form/job-contact-form.component'
          ).then(m => m.JobContactFormComponent),
        canActivate: [authGuard],
      },
      {
        path: 'jobs/:id/apply/start',
        loadComponent: () =>
          import('./pages/apply-choice/apply-choice.component').then(
            m => m.ApplyChoiceComponent
          ),
        canActivate: [applyChoiceGuard],
        data: { kind: 'job' },
      },
      {
        path: 'jobs/:id/apply/public',
        loadComponent: () =>
          import(
            './pages/jobs/job-apply-public/job-apply-public.component'
          ).then(m => m.JobApplyPublicComponent),
      },
      {
        path: 'job-alerts/confirm',
        loadComponent: () =>
          import('./pages/job-alerts/job-alert-token.component').then(
            m => m.JobAlertTokenComponent
          ),
        data: { mode: 'confirm' },
      },
      {
        path: 'job-alerts/unsubscribe',
        loadComponent: () =>
          import('./pages/job-alerts/job-alert-token.component').then(
            m => m.JobAlertTokenComponent
          ),
        data: { mode: 'unsubscribe' },
      },
      //#endregion
      //#region application
      {
        path: 'applications/my',
        loadComponent: () =>
          import(
            './pages/applications/my-applications/my-applications.component'
          ).then(m => m.MyApplicationsComponent),
        data: { archived: false },
        canActivate: [authGuard],
      },
      {
        path: 'applications/my/archived',
        loadComponent: () =>
          import(
            './pages/applications/my-applications/my-applications.component'
          ).then(m => m.MyApplicationsComponent),
        data: { archived: true },
        canActivate: [authGuard],
      },
      {
        path: 'received-applications/:id',
        loadComponent: () =>
          import(
            './pages/applications/received-application-detail/received-application-detail.component'
          ).then(m => m.ReceivedApplicationDetailComponent),
        canActivate: [authGuard],
        canMatch: [featureGuard('applications_v2')],
        canDeactivate: [confirmLeaveGuard],
      },
      {
        path: 'applications/:id',
        loadComponent: () =>
          import(
            './pages/applications/application-detail/application-detail.component'
          ).then(m => m.ApplicationDetailComponent),
        // TODO: only visible to school owner or applicant
        canActivate: [authGuard],
      },
      {
        path: 'applications/my/documents',
        loadComponent: () =>
          import(
            './pages/applications/application-documents/application-documents.component'
          ).then(m => m.ApplicationDocumentsComponent),
        canActivate: [authGuard],
      },
      //#endregion
      //#region settings
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/settings/settings.component').then(
            m => m.SettingsComponent
          ),
        canActivate: [authGuard],
      },
      {
        path: 'settings/account',
        loadComponent: () =>
          import(
            './pages/settings/settings-account/settings-account.component'
          ).then(m => m.SettingsAccountComponent),
        canActivate: [authGuard],
      },
      {
        path: 'settings/change-email',
        loadComponent: () =>
          import(
            './pages/settings/settings-change-email/settings-change-email.component'
          ).then(m => m.SettingsChangeEmailComponent),
        canActivate: [authGuard],
      },
      {
        path: 'settings/change-name',
        loadComponent: () =>
          import(
            './pages/settings/settings-change-name/settings-change-name.component'
          ).then(m => m.SettingsChangeNameComponent),
        canActivate: [authGuard],
      },
      {
        path: 'settings/change-name/info',
        loadComponent: () =>
          import(
            './pages/settings/settings-change-name-info/settings-change-name-info.component'
          ).then(m => m.SettingsChangeNameInfoComponent),
        canActivate: [authGuard],
      },
      {
        path: 'settings/change-password',
        loadComponent: () =>
          import(
            './pages/settings/settings-change-password/settings-change-password.component'
          ).then(m => m.SettingsChangePasswordComponent),
        canActivate: [authGuard],
      },
      {
        path: 'settings/change-password/secure-password',
        loadComponent: () =>
          import(
            './pages/settings/settings-change-password-secure-help/settings-change-password-secure-help.component'
          ).then(m => m.SettingsChangePasswordSecureHelpComponent),
        canActivate: [authGuard],
      },
      {
        path: 'settings/change-contact',
        loadComponent: () =>
          import(
            './pages/settings/settings-contact/settings-contact.component'
          ).then(m => m.SettingsContactComponent),
        canActivate: [authGuard],
      },
      {
        path: 'settings/delete-account',
        loadComponent: () =>
          import(
            './pages/settings/settings-delete-account/settings-delete-account.component'
          ).then(m => m.SettingsDeleteAccountComponent),
        canActivate: [authGuard],
      },
      {
        path: 'settings/marketing',
        loadComponent: () =>
          import(
            './pages/settings/settings-marketing/settings-marketing.component'
          ).then(m => m.SettingsMarketingComponent),
        canActivate: [authGuard],
      },
      {
        path: 'settings/privacy',
        loadComponent: () =>
          import(
            './pages/settings/settings-privacy/settings-privacy.component'
          ).then(m => m.SettingsPrivacyComponent),
        canActivate: [authGuard],
      },
      {
        path: 'settings/subscription',
        loadComponent: () =>
          import(
            './pages/settings/settings-subscription/settings-subscription.component'
          ).then(m => m.SettingsSubscriptionComponent),
        canActivate: [authGuard],
      },
      {
        path: 'settings/templates',
        loadComponent: () =>
          import(
            './pages/settings/settings-templates/settings-templates.component'
          ).then(m => m.SettingsTemplatesComponent),
        canActivate: [authGuard],
      },
      {
        path: 'settings/rights',
        loadComponent: () =>
          import(
            './pages/settings/settings-admin-rights/settings-admin-rights.component'
          ).then(m => m.SettingsAdminRightsComponent),
        canActivate: [authGuard, schoolAdminGuard],
      },
      {
        path: 'search-agent',
        loadComponent: () =>
          import('./pages/search-agent/search-agent.component').then(
            m => m.SearchAgentComponent
          ),
        canActivate: [authGuard],
      },
      {
        path: 'stellenchats',
        loadComponent: () =>
          import('./pages/stellenchats/stellenchats.component').then(
            m => m.StellenchatsComponent
          ),
      },
      //#endregion
      //#region Admin-Board (Staff)
      {
        path: 'admin',
        loadChildren: () =>
          import('./pages/admin/admin.routes').then(m => m.ADMIN_ROUTES),
        canActivate: [authGuard],
      },
      //#endregion
      {
        path: 'goodbye',
        loadComponent: () =>
          import('./pages/goodbye/goodbye.component').then(
            m => m.GoodbyeComponent
          ),
      },
      {
        path: 'auth-edulog-callback',
        loadComponent: () =>
          import('./pages/auth/edulog-callback.component').then(
            m => m.EdulogCallbackComponent
          ),
      },
      {
        path: 'auth/action',
        loadComponent: () =>
          import('./pages/auth/auth-action/auth-action.component').then(
            m => m.AuthActionComponent
          ),
      },
      //#endregion
      {
        path: 'chat',
        loadChildren: () =>
          import('../modules/chat/chat.module').then(m => m.ChatModule),
        canActivate: [authGuard],
      },
    ],
  },
  {
    // Chrome-free print layouts. PrintService loads them in a hidden iframe and
    // prints them; on iOS they open in a new tab with `?autoprint`.
    path: 'print/jobs/:id',
    loadComponent: () =>
      import('./pages/print/job-print/job-print.component').then(
        m => m.JobPrintComponent
      ),
  },
  {
    path: 'print/schools/:id',
    loadComponent: () =>
      import('./pages/print/school-print/school-print.component').then(
        m => m.SchoolPrintComponent
      ),
  },
  {
    path: 'verify-email',
    loadComponent: () =>
      import('./pages/verify-email/verify-email.component').then(
        m => m.VerifyEmailComponent
      ),
    canActivate: [verifyEmailGuard],
  },
  {
    path: 'welcome',
    loadComponent: () =>
      import('./pages/welcome/welcome.component').then(m => m.WelcomeComponent),
    canActivate: [requireVerifiedGuard],
  },
  {
    path: 'onboarding',
    loadComponent: () =>
      import('./pages/profiles/profile-wizard/profile-wizard.component').then(
        m => m.ProfileWizardComponent
      ),
    canActivate: [requireVerifiedGuard],
  },
  {
    path: 'onboarding/school',
    loadComponent: () =>
      import(
        './pages/onboarding/school-onboarding/school-onboarding.component'
      ).then(m => m.SchoolOnboardingWizardComponent),
    canActivate: [requireVerifiedGuard],
    children: [
      {
        path: 'complete-order',
        loadComponent: () =>
          import(
            './pages/onboarding/complete-order-onboarding/complete-order-onboarding.component'
          ).then(m => m.CompleteOrderOnboardingComponent),
      },
    ],
  },
  {
    path: 'onboarding/school/plan-success',
    loadComponent: () =>
      import(
        './pages/onboarding/school-plan-order-success/school-plan-order-success.component'
      ).then(m => m.SchoolPlanOrderSuccessComponent),
    canActivate: [requireVerifiedGuard],
  },
  {
    path: 'onboarding/setup',
    loadComponent: () =>
      import('./pages/onboarding/setup-progress/setup-progress.component').then(
        m => m.SetupProgressComponent
      ),
    canActivate: [requireVerifiedGuard],
  },
  {
    path: 'onboarding/municipality/plan-success',
    loadComponent: () =>
      import(
        './pages/onboarding/municipality-plan-order-success/municipality-plan-order-success.component'
      ).then(m => m.MunicipalityPlanOrderSuccessComponent),
    canActivate: [requireVerifiedGuard],
  },
  {
    path: 'onboarding/municipality/authority-profile',
    loadComponent: () =>
      import(
        './pages/onboarding/municipality-authority-profile-wizard/municipality-authority-profile-wizard.component'
      ).then(m => m.MunicipalityAuthorityProfileWizardComponent),
    canActivate: [requireVerifiedGuard],
  },
  {
    path: 'onboarding/municipality',
    loadComponent: () =>
      import(
        './pages/onboarding/municipality-onboarding/municipality-onboarding.component'
      ).then(m => m.MunicipalityOnboardingComponent),
    canActivate: [requireVerifiedGuard],
    children: [
      {
        path: 'plan',
        loadComponent: () =>
          import(
            './pages/onboarding/municipality-plan-onboarding/municipality-plan-onboarding.component'
          ).then(m => m.MunicipalityPlanOnboardingComponent),
      },
      {
        path: 'complete-order',
        loadComponent: () =>
          import(
            './pages/onboarding/complete-order-onboarding/complete-order-onboarding.component'
          ).then(m => m.CompleteOrderOnboardingComponent),
      },
    ],
  },
  {
    path: 'checkout/mock',
    loadComponent: () =>
      import('./pages/checkout/mock-stripe-checkout.component').then(
        m => m.MockStripeCheckoutComponent
      ),
    canActivate: [requireVerifiedGuard],
  },
  {
    path: 'checkout/success',
    loadComponent: () =>
      import('./pages/checkout/checkout-success.component').then(
        m => m.CheckoutSuccessComponent
      ),
    canActivate: [requireVerifiedGuard],
  },
  {
    path: '',
    component: EmbedLayoutComponent,
    children: [
      { path: '', redirectTo: '/embed', pathMatch: 'full' },
      {
        // PUBLIC EMBED CONTRACT — these `/embed/...` URLs are live inside <iframe>s on
        // third-party customer websites (schools, municipalities, associations like the
        // Verband Zürich / VSLCH). Treat the paths and query params as a stable public
        // API: do not rename, relocate, or change their param names without a migration.
        // They MUST keep rendering under the chrome-free EmbedLayout. See docs/embedding.md
        // for the documented contract and e2e/embed.e2e.ts for the guard tests.
        path: 'embed',
        children: [
          {
            path: 'schools',
            loadComponent: () =>
              import(
                './pages/embed/embed-schools/embed-schools.component'
              ).then(m => m.EmbedSchoolsComponent),
          },
          {
            path: 'schools-with-filters',
            loadComponent: () =>
              import('./pages/schools/schools.component').then(
                m => m.SchoolsComponent
              ),
            data: { embedded: true },
          },
          {
            // Chrome-free single-school profile for customer iframes (e.g. schule-rain.ch).
            // Same component as the in-app `/schools/:id`, but under EmbedLayout and with
            // in-app navigation (top bar, authority breadcrumb) suppressed via `embedded`.
            path: 'schools/:id',
            loadComponent: () =>
              import(
                './pages/schools/school-profile/school-profile.component'
              ).then(m => m.SchoolProfileComponent),
            data: { embedded: true },
          },
          {
            path: 'jobs',
            loadComponent: () =>
              import('./pages/embed/embed-jobs/embed-jobs.component').then(
                m => m.EmbedJobsComponent
              ),
          },
          {
            path: 'jobs-with-filters',
            loadComponent: () =>
              import('./pages/jobs/jobs.component').then(m => m.JobsComponent),
            data: { embedded: true },
          },
          {
            // Job detail and public apply inside the customer's iframe, so a visitor
            // never sees the Bildigsnetz app chrome. Embeds link here instead of
            // opening `/jobs/:id` in a new tab.
            path: 'jobs/:id',
            loadComponent: () =>
              import('./pages/jobs/job-detail/job-detail.component').then(
                m => m.JobDetailComponent
              ),
            data: { embedded: true },
          },
          {
            path: 'jobs/:id/apply/public',
            loadComponent: () =>
              import(
                './pages/jobs/job-apply-public/job-apply-public.component'
              ).then(m => m.JobApplyPublicComponent),
            data: { embedded: true },
          },
        ],
      },
    ],
    canActivate: [canActivateEmbedGuard],
  },
  { path: '**', redirectTo: '' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: PreloadAllModules,
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'disabled',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
